<template>
  <div class="account-page">
    <div class="account-container">
      <div class="card overview-card">
        <div class="profile-section">
          <div class="avatar-wrapper">
            <img src="@/assets/avatar.png" alt="Avatar" class="avatar-img" v-if="hasAvatar" />
            <div class="avatar-placeholder" v-else>{{ firstLetter }}</div>
          </div>
          <div class="user-info">
            <div class="name-row">
              <h3 class="user-name">{{ userStore.userInfo?.username || '未登录' }}</h3>
              <span class="uid-badge">UID: {{ mockData.userId }}</span>
            </div>
            <p class="meta-text">注册时间：{{ mockData.registerTime }}</p>
          </div>
        </div>

        <div class="vertical-divider"></div>

        <div class="asset-section">
          <div class="asset-info">
            <span class="asset-label">当前算力余额</span>
            <span class="number">{{ userStore.userInfo?.balance ?? '--' }}</span>
            <span class="unit">点</span>
          </div>
          <div class="sub-info" v-if="subActive">
            <span class="sub-badge">{{ subLabel }}</span>
            <span class="sub-expire" v-if="!subAdmin">到期：{{ subExpiry }}</span>
          </div>
          <button class="btn btn-primary push-right" @click="router.push('/payment')">获取算力</button>
        </div>
      </div>

      <div class="card security-card">
        <div class="card-header">
          <h3 class="card-title">安全设置</h3>
          <button class="btn-text" @click="router.push('/docs')">
            <i class="icon-docs"></i> 帮助文档
          </button>
        </div>

        <div class="security-list">
          <div class="list-item">
            <div class="item-main">
              <div class="item-title">账号密码</div>
              <div class="item-desc">用于保护账号信息和登录安全，建议定期修改</div>
            </div>
            <div class="item-action">
              <span class="status-indicator ok"><span class="dot"></span>已设置</span>
              <button class="btn-outline">修改密码</button>
            </div>
          </div>

          <div class="list-item">
            <div class="item-main">
              <div class="item-title">安全手机</div>
              <div class="item-desc">
                已绑定手机：<span class="highlight-text">{{ mockData.phone }}</span>
              </div>
            </div>
            <div class="item-action">
              <span class="status-indicator ok"><span class="dot"></span>已绑定</span>
              <button class="btn-outline">更换手机</button>
            </div>
          </div>

          <div class="list-item">
            <div class="item-main">
              <div class="item-title">安全邮箱</div>
              <div class="item-desc" :class="{ 'text-muted': !mockData.email }">
                {{ mockData.email ? `已绑定邮箱：${mockData.email}` : '未绑定，绑定后可用于找回密码和接收通知' }}
              </div>
            </div>
            <div class="item-action">
              <span class="status-indicator warn" v-if="!mockData.email"><span class="dot"></span>未绑定</span>
              <span class="status-indicator ok" v-else><span class="dot"></span>已绑定</span>
              <button class="btn-primary-outline" v-if="!mockData.email">去绑定</button>
              <button class="btn-outline" v-else>更换邮箱</button>
            </div>
          </div>
        </div>
      </div>

      <div class="actions-section">
        <button class="btn-danger" @click="handleLogout" :disabled="isLoggingOut">
          {{ isLoggingOut ? '正在退出...' : '退出登录' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/useAuthStore'

const router = useRouter()
const userStore = useAuthStore()

const isLoggingOut = ref(false)

const firstLetter = computed(() => {
  const name = userStore.userInfo?.username
  return name ? name.charAt(0).toUpperCase() : 'U'
})
const hasAvatar = ref(false)

const mockData = ref({
  userId: '100024',
  phone: '138****8888',
  email: '',
  registerTime: '2026-03-15 10:24:00'
})

const subActive = computed(() => userStore.userInfo?.subscriptionStatus === 'ACTIVE')
const subAdmin = computed(() => userStore.userInfo?.subscriptionPlan === 'ADMIN')
const subLabel = computed(() => {
  const plan = userStore.userInfo?.subscriptionPlan
  if (plan === 'ADMIN') return '管理员'
  if (plan === 'MONTHLY') return '月卡会员'
  if (plan === 'YEARLY') return '年卡会员'
  return '会员'
})
const subExpiry = computed(() => {
  const raw = userStore.userInfo?.subscriptionExpiresAt
  if (!raw) return ''
  try {
    return new Date(raw).toLocaleDateString('zh-CN')
  } catch { return raw }
})

const handleLogout = async () => {
  if (isLoggingOut.value) return
  isLoggingOut.value = true
  try {
    await userStore.logout()
  } catch (error) {
    console.error('登出失败:', error)
  } finally {
    isLoggingOut.value = false
    router.push('/login')
  }
}
</script>

<style scoped>
.account-page {
  padding: 2rem;
  background-color: #f8f9fb;
  min-height: calc(100vh - 64px);
  display: flex;
  flex-direction: column;
  align-items: center;
}

.page-header {
  width: 100%;
  max-width: 900px;
  margin-bottom: 1.5rem;
}

.page-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 0.5rem 0;
}

.page-subtitle {
  color: #666;
  font-size: 0.95rem;
  margin: 0;
}

.account-container {
  width: 100%;
  max-width: 900px;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.03);
  border: 1px solid #ebeef5;
  overflow: hidden;
}

/* 概览卡片 */
.overview-card {
  display: flex;
  align-items: center;
  padding: 2rem;
  gap: 2rem;
}

.profile-section {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.avatar-wrapper {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: #e6f0ff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.avatar-placeholder {
  font-size: 2rem;
  font-weight: bold;
  color: #1677ff;
}

.name-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
}

.user-name {
  margin: 0;
  font-size: 1.3rem;
  font-weight: 600;
  color: #333;
}

.uid-badge {
  background: #f0f2f5;
  color: #5c6b77;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.8rem;
  font-family: monospace;
}

.meta-text {
  margin: 0;
  color: #8c8c8c;
  font-size: 0.9rem;
}

.vertical-divider {
  width: 1px;
  height: 60px;
  background-color: #f0f0f0;
}

/* 算力资产区域 */
.asset-section {
  flex: 1;
  display: flex;
  align-items: center;
  padding-left: 1.5rem;
  width: 100%;
}

.asset-info {
  display: flex;
  align-items: baseline; 
  gap: 10px; 
}

.asset-label {
  font-size: 0.95rem;
  color: #666;
  white-space: nowrap;
}

.number {
  font-size: 1.8rem;
  font-weight: 700;
  color: #ff4d4f;
  line-height: 1;
}

.unit {
  font-size: 0.9rem;
  color: #8c8c8c;
}

.push-right {
  margin-left: auto;
}

.sub-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.3rem;
}
.sub-badge {
  background: linear-gradient(135deg, #ffd700, #ffaa00);
  color: #7b5f00;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
}
.sub-expire {
  color: #999;
  font-size: 0.8rem;
}

/* 安全设置卡片 */
.security-card {
  padding: 0;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #f0f0f0;
  background-color: #fafbfc;
}

.card-title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
}

.security-list {
  display: flex;
  flex-direction: column;
}

.list-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.2s;
}

.list-item:last-child {
  border-bottom: none;
}

.list-item:hover {
  background-color: #fafbfc;
}

.item-title {
  font-size: 1rem;
  font-weight: 500;
  color: #333;
  margin-bottom: 0.4rem;
}

.item-desc {
  font-size: 0.9rem;
  color: #666;
}

.highlight-text {
  color: #333;
  font-weight: 500;
}

.text-muted {
  color: #bfbfbf;
}

.item-action {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  min-width: 200px;
  justify-content: flex-end;
}

/* 状态指示器 */
.status-indicator {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.9rem;
}

.dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
}

.status-indicator.ok {
  color: #52c41a;
}
.status-indicator.ok .dot {
  background-color: #52c41a;
}

.status-indicator.warn {
  color: #faad14;
}
.status-indicator.warn .dot {
  background-color: #faad14;
}

/* 按钮与操作区 */
.btn {
  padding: 0.6rem 1.2rem;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  transition: all 0.2s;
  font-size: 0.9rem;
}

.btn-primary {
  background: #1677ff;
  color: #fff;
}
.btn-primary:hover {
  background: #4096ff;
}

.btn-primary-outline {
  padding: 0.4rem 1rem;
  border-radius: 6px;
  background: #e6f0ff;
  color: #1677ff;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.9rem;
}
.btn-primary-outline:hover {
  background: #1677ff;
  color: #fff;
}

.btn-outline {
  padding: 0.4rem 1rem;
  border-radius: 6px;
  background: #fff;
  color: #333;
  border: 1px solid #d9d9d9;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.9rem;
}
.btn-outline:hover {
  border-color: #1677ff;
  color: #1677ff;
}

.btn-text {
  background: transparent;
  border: none;
  color: #1677ff;
  cursor: pointer;
  font-size: 0.9rem;
}
.btn-text:hover {
  color: #4096ff;
}

.actions-section {
  display: flex;
  justify-content: center;
  margin-top: 1rem;
}

.btn-danger {
  padding: 0.6rem 2rem;
  background: #fff;
  border: 1px solid #ff4d4f;
  color: #ff4d4f;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  font-weight: 500;
}
.btn-danger:hover {
  background: #ff4d4f;
  color: #fff;
}
.btn-danger:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* 响应式调整 */
@media (max-width: 768px) {
  .overview-card {
    flex-direction: column;
    align-items: flex-start;
    gap: 1.5rem;
  }
  .vertical-divider {
    width: 100%;
    height: 1px;
  }
  .asset-section {
    width: 100%;
    padding-left: 0;
  }
  .list-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
  .item-action {
    width: 100%;
    justify-content: space-between;
  }
}
</style>